import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import localforage from 'localforage';
import axios from 'axios';
import { 
  User, UserX, UserCheck, AlertTriangle, RefreshCw, 
  Search, Save, X, Heart, HeartOff, Shield 
} from 'lucide-react';

// Base URL for API endpoints
const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';

const AdminSickManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [processingUser, setProcessingUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // File management state variables
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [phaseFiles, setPhaseFiles] = useState([]);
  const [uploadForm, setUploadForm] = useState({ file: null, description: '', category: '', isPublic: false });
  const [editForm, setEditForm] = useState({ description: '', category: '', isPublic: false });
  const [phaseUploadForm, setPhaseUploadForm] = useState({ file: null, description: '', category: '' });
  const [fileToEdit, setFileToEdit] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [bulkActionFiles, setBulkActionFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [success, setSuccess] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showPhaseUploadModal, setShowPhaseUploadModal] = useState(false);
  const [token, setToken] = useState(null);

  // Load token on mount
  useEffect(() => {
    const loadToken = async () => {
      const storedToken = await localforage.getItem('token');
      setToken(storedToken);
    };
    loadToken();
  }, []);

  // Fetch files function
  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/api/files`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Handle files data
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Mark user as healthy
  const markUserAsHealthy = async (userId) => {
    try {
      setProcessingUser(userId);
      const storedToken = await localforage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/admin/users/${userId}/sick`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isSick: false })
      });
      if (response.ok) {
        setSuccessMessage('User marked as healthy');
        fetchUsers();
      }
    } catch (err) {
      console.error('Error marking user as healthy:', err);
      setError(err.message);
    } finally {
      setProcessingUser(null);
    }
  };

  // Mark user as sick
  const markUserAsSick = async (userId) => {
    try {
      setProcessingUser(userId);
      const storedToken = await localforage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/admin/users/${userId}/sick`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isSick: true, sickReason: 'Marked by admin' })
      });
      if (response.ok) {
        setSuccessMessage('User marked as sick');
        fetchUsers();
      }
    } catch (err) {
      console.error('Error marking user as sick:', err);
      setError(err.message);
    } finally {
      setProcessingUser(null);
    }
  };

  // Fetch all users with their sick status
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const storedToken = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/admin/users/sick-status`, {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch files for the selected phase
  const fetchPhaseFiles = async () => {
    if (!selectedPhaseId) {
      setPhaseFiles([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${BASE_URL}/api/files/phase/${selectedPhaseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 200) {
        const fetchedFiles = response.data.files || [];
        
        // Add animation delay indices for staggered animation
        const filesWithDelay = fetchedFiles.map((file, index) => ({
          ...file,
          animationDelay: index * 50
        }));
        
        setPhaseFiles(filesWithDelay);
      }
    } catch (err) {
      console.error('Error fetching phase files:', err);
      setError('Failed to load phase files. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // ==========================================================================
  // DATA OPERATIONS - GENERAL FILES
  // ==========================================================================
  
  // Handle file upload
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      setError('Please select a file to upload');
      return;
    }
    
    if (!uploadForm.description.trim()) {
      setError('Please enter a description');
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('isPublic', uploadForm.isPublic);
      
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(key, typeof value === 'object' ? 'File' : value);
      }
      
      // WICHTIG: Verwende fetch() wie in CampOverview.js, NICHT axios!
      const response = await fetch(`${BASE_URL}/api/files/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // DO NOT set Content-Type for FormData - Browser setzt es automatisch mit boundary
        },
        body: formData
      });
      
      console.log('Upload response status:', response.status);
      
      if (response.ok) {
        // Reset form and close modal
        setUploadForm({
          file: null,
          description: '',
          category: 'Documents',
          isPublic: true
        });
        
        setShowUploadModal(false);
        setSuccess('File uploaded successfully!');
        
        // Refresh file list
        fetchFiles();
      }
      
    } catch (err) {
      console.error('Error uploading file:', err);
      
      let errorMessage = 'Failed to upload file. Please try again.';
      
      // Handle fetch errors
      if (err.message) {
        errorMessage = `Network error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Handle file edit/update
  const handleEdit = async (e) => {
    e.preventDefault();
    
    if (!fileToEdit) return;
    
    try {
      setLoading(true);
      
      const formData = new URLSearchParams();
      formData.append('description', editForm.description);
      formData.append('category', editForm.category);
      formData.append('isPublic', editForm.isPublic);
      
      const response = await axios.put(
        `${BASE_URL}/api/files/${fileToEdit.id}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );
      
      if (response.status === 200) {
        setSuccess('File updated successfully!');
        
        // Close modal and refresh list
        setShowEditModal(false);
        setFileToEdit(null);
        fetchFiles();
      }
      
    } catch (err) {
      console.error('Error marking user as sick:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file deletion
  const handleDelete = async () => {
    if (!fileToDelete) return;
    
    try {
      setLoading(true);
      
      const response = await axios.delete(
        `${BASE_URL}/api/files/${fileToDelete.id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.status === 200) {
        setSuccess('File deleted successfully!');
        
        // Close modal and refresh list
        setShowDeleteModal(false);
        setFileToDelete(null);
        fetchFiles();
      }
      
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Failed to delete file. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle bulk deletion
  const handleBulkDelete = async () => {
    if (bulkActionFiles.length === 0) return;
    
    try {
      setLoading(true);
      
      const deletePromises = bulkActionFiles.map(fileId => 
        axios.delete(`${BASE_URL}/api/files/${fileId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      );
      
      await Promise.all(deletePromises);
      
      setSuccess(`${bulkActionFiles.length} files deleted successfully!`);
      
      // Clear selection and refresh list
      setBulkActionFiles([]);
      setShowBulkActions(false);
      fetchFiles();
      
    } catch (err) {
      console.error('Error deleting files in bulk:', err);
      setError('Failed to delete some files. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle bulk visibility change
  const handleBulkVisibilityChange = async (makePublic) => {
    if (bulkActionFiles.length === 0) return;
    
    try {
      setLoading(true);
      
      const updatePromises = bulkActionFiles.map(fileId => {
        const formData = new URLSearchParams();
        formData.append('isPublic', makePublic);
        
        return axios.put(
          `${BASE_URL}/api/files/${fileId}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          }
        );
      });
      
      await Promise.all(updatePromises);
      
      setSuccess(`Visibility updated for ${bulkActionFiles.length} files!`);
      
      // Clear selection and refresh list
      setBulkActionFiles([]);
      setShowBulkActions(false);
      fetchFiles();
      
    } catch (err) {
      console.error('Error updating visibility in bulk:', err);
      setError('Failed to update visibility for some files. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // ==========================================================================
  // DATA OPERATIONS - PHASE FILES
  // ==========================================================================
  
  // Handle phase file upload
  const handlePhaseUpload = async (e) => {
    e.preventDefault();
    
    if (!phaseUploadForm.file) {
      setError('Please select a file to upload');
      return;
    }
    
    if (!phaseUploadForm.description.trim()) {
      setError('Please enter a description');
      return;
    }
    
    if (!selectedPhaseId) {
      setError('No phase selected. Please select a phase before uploading.');
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', phaseUploadForm.file);
      formData.append('description', phaseUploadForm.description);
      formData.append('isPublic', phaseUploadForm.isPublic);
      
      console.log('Phase upload FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(key, typeof value === 'object' ? 'File' : value);
      }
      
      // WICHTIG: Verwende fetch() wie in CampOverview.js, NICHT axios!
      const response = await fetch(`${BASE_URL}/api/files/phase/${selectedPhaseId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // DO NOT set Content-Type for FormData - Browser setzt es automatisch mit boundary
        },
        body: formData
      });
      
      console.log('Phase upload response status:', response.status);
      
      if (response.ok) {
        // Reset form and close modal
        setPhaseUploadForm({
          file: null,
          description: '',
          isPublic: true
        });
        
        setShowPhaseUploadModal(false);
        setSuccess('File uploaded to phase successfully!');
        
        // Refresh file list
        fetchPhaseFiles();
      }
      
    } catch (err) {
      console.error('Error uploading phase file:', err);
      
      let errorMessage = 'Failed to upload file to phase. Please try again.';
      
      // Handle fetch errors
      if (err.message) {
        errorMessage = `Network error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setProcessingUser(null);
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    fetchUsers();
  }, []);

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: '#f8fafc'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '30px',
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    searchContainer: {
      position: 'relative',
      marginBottom: '20px'
    },
    searchInput: {
      width: '100%',
      padding: '12px 16px 12px 40px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#6b7280'
    },
    userGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '20px'
    },
    userCard: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      position: 'relative'
    },
    userCardSick: {
      backgroundColor: '#fef2f2',
      border: '2px solid #fca5a5'
    },
    userInfo: {
      marginBottom: '15px'
    },
    userName: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '5px'
    },
    userEmail: {
      fontSize: '14px',
      color: '#6b7280'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: 'bold',
      marginBottom: '10px'
    },
    sickBadge: {
      backgroundColor: '#fee2e2',
      color: '#dc2626'
    },
    healthyBadge: {
      backgroundColor: '#dcfce7',
      color: '#16a34a'
    },
    actionButton: {
      width: '100%',
      padding: '10px 16px',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    sickButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    healthyButton: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    sickInfo: {
      fontSize: '12px',
      color: '#6b7280',
      marginTop: '10px',
      padding: '8px',
      backgroundColor: '#f3f4f6',
      borderRadius: '6px'
    },
    successMessage: {
      backgroundColor: '#dcfce7',
      border: '1px solid #16a34a',
      color: '#16a34a',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      border: '1px solid #dc2626',
      color: '#dc2626',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255,255,255,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '12px'
    },
    refreshButton: {
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      padding: '10px 16px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'background-color 0.2s'
    }
  };

  if (loading && users.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>
            <Heart size={24} />
            Krank melden - Admin Dashboard
          </h1>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <RefreshCw size={32} className="animate-spin" />
          <p>Lade User-Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <Heart size={24} />
          Krank melden - Admin Dashboard
        </h1>
        <button 
          style={styles.refreshButton}
          onClick={fetchUsers}
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Aktualisieren
        </button>
      </div>

      {/* Success/Error Messages */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            style={styles.successMessage}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <UserCheck size={16} />
            {successMessage}
          </motion.div>
        )}
        
        {error && (
          <motion.div
            style={styles.errorMessage}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <AlertTriangle size={16} />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search */}
      <div style={styles.searchContainer}>
        <Search size={20} style={styles.searchIcon} />
        <input
          type="text"
          placeholder="User suchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            ...styles.searchInput,
            borderColor: searchQuery ? '#3b82f6' : '#e5e7eb'
          }}
        />
      </div>

      {/* User Grid */}
      <div style={styles.userGrid}>
        {filteredUsers.map(user => (
          <motion.div
            key={user.id}
            style={{
              ...styles.userCard,
              ...(user.isSick ? styles.userCardSick : {})
            }}
            whileHover={{ y: -2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
            layout
          >
            {/* Processing Overlay */}
            {processingUser === user.id && (
              <div style={styles.loadingOverlay}>
                <RefreshCw size={24} className="animate-spin" />
              </div>
            )}
            
            {/* User Info */}
            <div style={styles.userInfo}>
              <div style={styles.userName}>{user.username}</div>
              <div style={styles.userEmail}>{user.email}</div>
              
              {/* Status Badge */}
              <div style={{
                ...styles.statusBadge,
                ...(user.isSick ? styles.sickBadge : styles.healthyBadge)
              }}>
                {user.isSick ? (
                  <>
                    <HeartOff size={12} />
                    Krank
                  </>
                ) : (
                  <>
                    <UserCheck size={12} />
                    Gesund
                  </>
                )}
              </div>
            </div>

            {/* Sick Info */}
            {user.isSick && (
              <div style={styles.sickInfo}>
                <strong>Grund:</strong> {user.sickReason || 'Kein Grund angegeben'}<br />
                <strong>Gemeldet von:</strong> {user.sickMarkedBy}<br />
                {user.sickTimestamp && (
                  <>
                    <strong>Zeitpunkt:</strong> {new Date(user.sickTimestamp).toLocaleDateString('de-DE')}
                  </>
                )}
              </div>
            )}

            {/* Action Button */}
            {user.isSick ? (
              <button
                style={{
                  ...styles.actionButton,
                  ...styles.healthyButton
                }}
                onClick={() => markUserAsHealthy(user.id)}
                disabled={processingUser === user.id}
              >
                <UserCheck size={16} />
                Als gesund melden
              </button>
            ) : (
              <button
                style={{
                  ...styles.actionButton,
                  ...styles.sickButton
                }}
                onClick={() => markUserAsSick(user.id)}
                disabled={processingUser === user.id}
              >
                <HeartOff size={16} />
                Als krank melden
              </button>
            )}
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <User size={48} style={{ margin: '0 auto 16px' }} />
          <p>Keine User gefunden.</p>
        </div>
      )}
    </div>
  );
};

export default AdminSickManagement;